import React, { useState, useCallback } from 'react';
import {
  Upload,
  Button,
  Card,
  Typography,
  Alert,
  Progress,
  Table,
  Space,
  Tag,
  Select,
  message,
  Divider,
  Tabs,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  FormOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import axios from 'axios';
import { config } from '../config/env';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import ManualDataEntry from './ManualDataEntry';

const { Title, Text } = Typography;
const { Option } = Select;

interface CSVParseResult {
  valid: any[];
  invalid: any[];
  errors: string[];
  metadata: {
    totalRows: number;
    validRows: number;
    invalidRows: number;
    columns: string[];
    detectedTypes: { [column: string]: string };
  };
}

export const DataUpload: React.FC = () => {
  const { user } = useAuth();
  const { lat, lng } = useLocation();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [parseResult, setParseResult] = useState<CSVParseResult | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string | null>(null);

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${config.apiUrl}/api/data/energy/csv-template`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'energy-data-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Template downloaded successfully');
    } catch (error: any) {
      message.error(`Failed to download template: ${error.message}`);
    }
  };

  const handleFileChange: UploadProps['onChange'] = (info) => {
    setFileList(info.fileList);
    setParseResult(null);
    setJobId(null);
    setJobStatus(null);

    if (info.file.status === 'done') {
      message.success(`${info.file.name} file uploaded successfully`);
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  const customRequest = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;

    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${config.apiUrl}/api/data/energy/upload-csv`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              setUploadProgress(percent);
              onProgress({ percent });
            }
          },
        },
      );

      setParseResult(response.data.parseResult);
      setJobId(response.data.jobId);
      setJobStatus(response.data.status);
      setUploadProgress(100);
      onSuccess(response.data, file);
      message.success('CSV uploaded and processed successfully');
    } catch (error: any) {
      onError(error);
      message.error(`Upload failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const checkJobStatus = async () => {
    if (!jobId) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${config.apiUrl}/api/data/job/${jobId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setJobStatus(response.data.job.status);
      
      if (response.data.job.status === 'COMPLETED') {
        message.success(
          `Processing complete: ${response.data.job.recordsProcessed} records processed`,
        );
      } else if (response.data.job.status === 'FAILED') {
        message.error(`Processing failed: ${response.data.job.error}`);
      }
    } catch (error: any) {
      message.error(`Failed to check job status: ${error.message}`);
    }
  };

  const previewColumns = parseResult?.metadata.columns.map((col) => ({
    title: col,
    dataIndex: col,
    key: col,
    render: (text: any) => {
      const type = parseResult.metadata.detectedTypes[col];
      if (type === 'number') {
        return parseFloat(text).toFixed(2);
      }
      return text;
    },
  }));

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <Title level={2}>Upload Energy Data</Title>
      <Text type="secondary">
        Upload CSV files or enter data manually (solar, wind, battery, grid status)
      </Text>

      <Card style={{ marginTop: '24px' }}>
        <Tabs
          defaultActiveKey="csv"
          items={[
            {
              key: 'csv',
              label: (
                <span>
                  <UploadOutlined /> CSV Upload
                </span>
              ),
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={handleDownloadTemplate}
                      style={{ marginBottom: '16px' }}
                    >
                      Download CSV Template
                    </Button>
                    <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
                      Download a template CSV file to see the required format
                    </Text>
                  </div>

                  <Upload
                    fileList={fileList}
                    onChange={handleFileChange}
                    customRequest={customRequest}
                    accept=".csv"
                    maxCount={1}
                    beforeUpload={() => false}
                  >
                    <Button icon={<UploadOutlined />} loading={uploading} disabled={uploading}>
                      {uploading ? 'Uploading...' : 'Select CSV File'}
                    </Button>
                  </Upload>

                  {uploading && <Progress percent={uploadProgress} status="active" />}

                  {parseResult && (
                    <div>
                      <Divider orientation="left">Parse Results</Divider>
                      <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <div>
                          <Text strong>Total Rows: </Text>
                          <Text>{parseResult.metadata.totalRows}</Text>
                          <br />
                          <Text strong>Valid Rows: </Text>
                          <Tag color="success">
                            <CheckCircleOutlined /> {parseResult.metadata.validRows}
                          </Tag>
                          <br />
                          <Text strong>Invalid Rows: </Text>
                          <Tag color="error">
                            <CloseCircleOutlined /> {parseResult.metadata.invalidRows}
                          </Tag>
                        </div>

                        {parseResult.errors.length > 0 && (
                          <Alert
                            message="Validation Errors"
                            description={
                              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                {parseResult.errors.slice(0, 10).map((error, idx) => (
                                  <li key={idx}>{error}</li>
                                ))}
                                {parseResult.errors.length > 10 && (
                                  <li>... and {parseResult.errors.length - 10} more errors</li>
                                )}
                              </ul>
                            }
                            type="warning"
                            showIcon
                          />
                        )}

                        <div>
                          <Text strong>Detected Columns:</Text>
                          <div style={{ marginTop: '8px' }}>
                            {parseResult.metadata.columns.map((col) => (
                              <Tag key={col} style={{ marginBottom: '4px' }}>
                                {col} ({parseResult.metadata.detectedTypes[col]})
                              </Tag>
                            ))}
                          </div>
                        </div>

                        {parseResult.valid.length > 0 && (
                          <div>
                            <Text strong>Preview (First 5 Valid Records):</Text>
                            <Table
                              dataSource={parseResult.valid.slice(0, 5).map((record, idx) => ({
                                ...record,
                                key: idx,
                              }))}
                              columns={previewColumns}
                              pagination={false}
                              size="small"
                              style={{ marginTop: '8px' }}
                            />
                          </div>
                        )}

                        {jobId && (
                          <div>
                            <Text strong>Processing Status: </Text>
                            <Tag color={jobStatus === 'COMPLETED' ? 'success' : 'processing'}>
                              {jobStatus}
                            </Tag>
                            {jobStatus !== 'COMPLETED' && jobStatus !== 'FAILED' && (
                              <Button
                                size="small"
                                onClick={checkJobStatus}
                                style={{ marginLeft: '8px' }}
                              >
                                Check Status
                              </Button>
                            )}
                          </div>
                        )}
                      </Space>
                    </div>
                  )}
                </Space>
              ),
            },
            {
              key: 'manual',
              label: (
                <span>
                  <FormOutlined /> Manual Entry
                </span>
              ),
              children: <ManualDataEntry />,
            },
          ]}
        />
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <Title level={4}>
          <FileTextOutlined /> CSV Format Requirements
        </Title>
        <ul>
          <li>
            <Text strong>Required columns:</Text> timestamp, solarOutput, windOutput,
            batteryLevel, gridStatus
          </li>
          <li>
            <Text strong>Timestamp format:</Text> ISO 8601 (YYYY-MM-DD HH:MM:SS) or
            YYYY-MM-DD
          </li>
          <li>
            <Text strong>Solar/Wind Output:</Text> Numbers in kW (0-10000 for solar,
            0-5000 for wind)
          </li>
          <li>
            <Text strong>Battery Level:</Text> Percentage (0-100)
          </li>
          <li>
            <Text strong>Grid Status:</Text> "importing", "exporting", or "neutral"
          </li>
          <li>
            Column names are auto-detected, but you can map them manually if needed
          </li>
        </ul>
      </Card>
    </div>
  );
};

export default DataUpload;
